/**
 * @fileoverview Integrations Section - "Connected to the tools your firm needs"
 *
 * Purpose: Highlight how Morgan connects to the systems, firm tools, and
 * precedents practices already rely on.
 *
 * Layout:
 * - Two-column header (mirrors the demo section): serif heading on the left,
 *   right-aligned supporting copy on the right.
 * - A row of three containers framed by horizontal lines (top + bottom) and
 *   separated by full-height vertical divider lines. Each container has a
 *   lighter-green animation placeholder on top with heading + copy below
 *   (mirrors the feature cards structure).
 *
 * Note: Animation components are intentionally not wired in yet — each
 * container's lighter-green placeholder is left clear so an animation can be
 * dropped in later.
 *
 * Theming: uses the global semantic CSS variables so the section adapts to
 * light and dark mode (the marketing footer exposes a theme switcher).
 *
 * Used by: Homepage (index.tsx)
 */

import { AritaLibraryAnimation } from "./-ui.arita-library-animation";
import { HistoryAnimation } from "./-ui.history-animation";
import { IntegrationsOrbit } from "./-ui.integrations-orbit";

/** Integration feature content. */
const INTEGRATION_CARDS = [
  {
    title: "Integrates with third party\ntools & firm systems.",
    description:
      "Connects to client accounting systems like Xero & MYOB & firm tools like SharePoint, OneDrive & Outlook, so matter data flows & syncs automatically.",
    hasOrbit: true,
  },
  {
    title: "ARITA precedents, built\ninto workflows.",
    description:
      "Access ARITA precedents, pre-configured to automate Day Ones & ongoing communications. Updates to precedents are automatically applied.",
    hasAritaLibrary: true,
  },
  {
    title: "Built for control, audit &\ncompliance.",
    description:
      "Full audit trail on every action, role-based access controls, & secure deployment aligned with your firm's existing controls.",
    hasHistory: true,
  },
] as const;

/**
 * Integrations section with a two-column header and three feature containers.
 */
export function IntegrationsSection() {
  return (
    <section className="relative w-full border-t border-primary/10 bg-background py-8 text-foreground md:py-24">
      <div className="mx-auto max-w-7xl px-6">
        {/* Header: heading left, supporting copy right */}
        <div className="grid grid-cols-1 items-start gap-10 md:grid-cols-2 md:gap-16">
          <div>
            <h2 className="max-w-xl text-3xl font-medium leading-[1.1] md:text-[42px]">
              Connected to the tools your <br className="hidden md:inline" />
              firm needs to run a matter.
            </h2>
          </div>

          <div className="leading-relaxed text-foreground/60 md:ml-auto md:max-w-sm md:text-right">
            <p className="text-base md:text-lg">
              Morgan integrates with the third party systems, firm tools, &amp;
              industry precedents your firm already runs on.
            </p>
          </div>
        </div>

        {/* Three containers framed by top + bottom lines, separated by
            full-height vertical divider lines */}
        <div className="mt-16 grid grid-cols-1 md:grid-cols-3 md:divide-x md:divide-primary/10 md:border-y md:border-primary/10">
          {INTEGRATION_CARDS.map((card) => (
            <div key={card.title} className="flex min-h-[520px] flex-col p-8">
              {/* Lighter-green animation placeholder (holds integration imagery).
                  Fixed height + overflow-hidden so no animation (absolute or
                  in-flow) can ever blow out the card height. */}
              <div className="relative flex h-[274px] items-center justify-center overflow-hidden rounded-lg bg-primary/[0.06] p-4">
                {"hasOrbit" in card && <IntegrationsOrbit />}
                {"hasAritaLibrary" in card && <AritaLibraryAnimation />}
                {"hasHistory" in card && <HistoryAnimation />}
              </div>

              <h3 className="mt-8 min-h-[66px] whitespace-normal text-2xl font-semibold leading-snug text-primary md:whitespace-pre-line">
                {card.title}
              </h3>
              <p className="mt-4 text-sm leading-relaxed text-foreground/60">
                {card.description}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
